import React, { useMemo } from "react";
import { Line, LineChart, XAxis, YAxis } from "recharts";
import { PerformanceCardInterface } from "../../interface/PerformanceCardInterface";
import { useAtom } from "jotai";
import { dashboardModeAtom } from "@/atoms";
import { useNewVsReturningState } from "@lib/hooks/url-state";
import { calculateRoundFactor } from "@components/ChannelControlMetrics/ConversionValueChart";
import { Tooltip, TooltipContent, TooltipTrigger } from "@tracify/ui/tooltip";
import RateDisplay from "./RateDisplay";
import InfoIcon from "@tracify/ui/info-icon";
import {
  metricsInformation,
  generalMetricsInformation,
} from "@components/MetricNameWithTooltip/metricsInformation";
import { COLUMN_OPTIONS, GENERAL_COLUMN_OPTIONS } from "@/constants/constants";
import { Card, CardContent, CardHeader, CardTitle } from "@tracify/ui/card";
import { cn } from "@/lib/utils";
import { cva, type VariantProps } from "class-variance-authority";
import { ChartConfig, ChartContainer } from "@tracify/ui/chart";

const performanceCardVariants = cva(
  "h-full flex flex-col lg:flex-row lg:items-center items-start lg:justify-between",
  {
    variants: {
      variant: {
        default: "",
        alternate: "bg-background",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
);

const chartContainerVariants = cva(
  "hidden lg:flex mb-4 lg:mb-0 lg:ml-4 lg:order-2 px-4 py-3 h-28 xl:h-full items-center rounded-lg box-border w-full lg:w-auto",
  {
    variants: {
      variant: {
        default: "bg-background",
        alternate: "bg-card",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
);

/**
 * Implements the reusable component for card.
 *
 * @param {PerformanceCardInterface} props
 * @return {CustomElement}
 */
export function PerformanceCard(
  props: PerformanceCardInterface & VariantProps<typeof performanceCardVariants>
) {
  const {
    title,
    value,
    comparedValue,
    rate,
    tooltip,
    chartData,
    isIncreasing,
    chartWidth,
    isReduced,
    label,
    compared,
    hideRate,
    dataAttributes,
    lineColor = "#6254FF",
    variant,
  } = props;

  const { newVsReturning } = useNewVsReturningState();
  const [dashboardMode] = useAtom(dashboardModeAtom);

  // Get the appropriate metrics information based on dashboard mode
  const metricsInfo =
    dashboardMode === "general"
      ? generalMetricsInformation
      : metricsInformation;

  // Chart config
  const chartConfig = useMemo<ChartConfig>(
    () => ({
      value: {
        label: "Value",
        color: lineColor,
      },
      comparedValue: {
        label: "Compared Value",
        color: lineColor,
      },
    }),
    [lineColor]
  );

  const maxValue = useMemo(() => {
    let max = 0;
    if (chartData) {
      for (const entry of chartData) {
        if (entry.value > max) {
          max = entry.value;
        }
      }
    }
    return max;
  }, [chartData]);
  const chartRoundFactor = useMemo(
    () => calculateRoundFactor(maxValue),
    [maxValue]
  );

  const transformLabel = (label: string) => {
    const transformConfig: {
      [key: string]: string;
    } = {
      ROAS: "Aquisition ROAS",
    };

    if (newVsReturning === "new" && transformConfig[label]) {
      return transformConfig[label];
    }

    return label;
  };

  const getTooltipContent = () => {
    // First check if there's a direct tooltip prop
    if (tooltip) {
      return tooltip;
    }

    // Then check if the title matches any column option label
    const matchingColumn = [...COLUMN_OPTIONS, ...GENERAL_COLUMN_OPTIONS].find(
      (column) => column.label === title
    );
    if (matchingColumn) {
      const metricKey = matchingColumn.value;
      const metricInfo = metricsInfo[metricKey as keyof typeof metricsInfo];
      if (metricInfo) {
        return metricInfo;
      }
    }

    // Finally fall back to the current behavior
    const metricKey = title;
    const titleToMetricMap: Record<string, string> = {
      MER: "return_on_ad_spend",
      CAC: "customer_acquisition_cost",
      CPO: "cost_per_order",
      CPA: "cost_per_order",
      "CTR (link)": "click_through_rate",
      "New Customer Rate": "new_customer_purchase_ratio",
      "Conversion Rate": "conversion_rate",
      "Ad Spend": "total_spend",
      "Store Revenue": "customer_revenue",
      "Conv. Value": "customer_revenue",
      storeRevenue: "marketingOverviewRevenue",
    };

    return metricsInfo[
      (titleToMetricMap[title] || metricKey) as keyof typeof metricsInfo
    ];
  };

  const titleWrap = label ? "flex-wrap" : "";

  const id = "kpi-" + title.split(" ").join("-").toLowerCase();

  return (
    <Card className={cn(performanceCardVariants({ variant }), "p-4")} id={id}>
      {chartData && (
        <div className={chartContainerVariants({ variant })}>
          <ChartContainer
            config={chartConfig}
            className="h-[60px]"
            style={{ width: chartWidth ?? 80 }}
          >
            <LineChart data={chartData} width={100} height={60}>
              <XAxis dataKey="key" hide />
              <YAxis
                hide
                yAxisId="1"
                tickLine={false}
                domain={[
                  0,
                  Math.ceil(maxValue / chartRoundFactor) * chartRoundFactor,
                ]}
              />
              <Line
                type="monotone"
                yAxisId="1"
                dataKey="value"
                strokeWidth={2}
                stroke="var(--color-value)"
                dot={false}
              />
              <Line
                type="monotone"
                yAxisId="1"
                dataKey="comparedValue"
                strokeWidth={2}
                stroke="var(--color-comparedValue)"
                strokeDasharray="5 5"
                dot={false}
              />
            </LineChart>
          </ChartContainer>
        </div>
      )}
      <div className="flex h-full flex-col items-start lg:order-1">
        <CardHeader className={cn("p-0 pb-2")}>
          <CardTitle
            className={cn(
              "text-sm lg:text-base text-foreground font-medium wrap-break-word flex items-center",
              titleWrap
            )}
          >
            <div className="mr-1">{transformLabel(title)}</div>
            {label && (
              <p className="text-foreground text-sm rounded-lg py-1 px-2 bg-muted inline">
                {label}
              </p>
            )}
            {tooltip
              ? tooltip
              : getTooltipContent() && (
                  <Tooltip>
                    <TooltipTrigger className="ml-1 flex">
                      <InfoIcon />
                    </TooltipTrigger>
                    <TooltipContent>
                      <p className="max-w-xs">{getTooltipContent()}</p>
                    </TooltipContent>
                  </Tooltip>
                )}
          </CardTitle>
        </CardHeader>
        <CardContent className={cn("p-0 flex-1")}>
          <div
            className={cn(
              isReduced ? "text-base lg:text-xl" : "text-lg lg:text-2xl",
              "text-foreground break-all font-bold mt-2"
            )}
            data-display={dataAttributes?.display}
          >
            {value}
          </div>
          {compared && (
            <Tooltip open={comparedValue ? undefined : false}>
              <TooltipTrigger>
                <RateDisplay
                  className="mt-2"
                  rate={rate}
                  isIncreasing={isIncreasing}
                  hideRate={hideRate}
                />
              </TooltipTrigger>
              <TooltipContent side="bottom">
                <p
                  className="text-sm text-foreground-soft"
                  data-display={dataAttributes?.display}
                  data-compared={true}
                >
                  Previously: {comparedValue}
                </p>
              </TooltipContent>
            </Tooltip>
          )}
        </CardContent>
      </div>
    </Card>
  );
}
